import type { AppointmentItem } from '../appointments/appointments-model'
import type { Conversation } from '../conversations/conversations-model'

export type CalendarView = 'day' | 'week' | 'month'
export type CalendarMode = 'schedule' | 'activity' | 'combined'
export type CalendarStatusFilter =
  | 'active'
  | 'all'
  | 'scheduled'
  | 'confirmed'
  | 'cancelled'
  | 'unknown'
  | 'flio'
/** How the Day lanes and the Week rows are grouped. */
export type CalendarGrouping = 'provider' | 'room'

export type CalendarActivity = {
  id: string
  aggregateId: string | null
  timestamp: number
  date: string
  displayName: string
  preview: string
  channel: Conversation['channel']
  timeZone: string | null
}

const DAY_MS = 86_400_000

function utcDate(value: string) {
  return new Date(`${value}T00:00:00Z`)
}

export function todayKey(nowMs = Date.now(), timeZone: string | null = null) {
  return calendarDateKey(nowMs, timeZone)
}

export function calendarDateKey(timestamp: number, timeZone: string | null) {
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone ?? 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } catch {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }
  const values = Object.fromEntries(
    formatter
      .formatToParts(timestamp)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
  return `${values.year}-${values.month}-${values.day}`
}

export function addCalendarDays(value: string, count: number) {
  return new Date(utcDate(value).getTime() + count * DAY_MS)
    .toISOString()
    .slice(0, 10)
}

export function weekFor(value: string) {
  const day = utcDate(value).getUTCDay()
  const start = addCalendarDays(value, day === 0 ? -6 : 1 - day)
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(start, index))
}

export function monthFor(value: string) {
  const date = utcDate(value)
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10)
  const firstDay = utcDate(first).getUTCDay()
  const start = addCalendarDays(first, firstDay === 0 ? -6 : 1 - firstDay)
  return Array.from({ length: 42 }, (_, index) => addCalendarDays(start, index))
}

export function stepCalendarDate(
  value: string,
  view: CalendarView,
  direction: -1 | 1
) {
  if (view === 'day') return addCalendarDays(value, direction)
  if (view === 'week') return addCalendarDays(value, direction * 7)
  const date = utcDate(value)
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + direction, 1)
  )
    .toISOString()
    .slice(0, 10)
}

export function calendarRangeLabel(value: string, view: CalendarView) {
  if (view === 'day') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(utcDate(value))
  }
  if (view === 'month') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'long',
      year: 'numeric',
    }).format(utcDate(value))
  }
  const week = weekFor(value)
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  })
  return `${formatter.format(utcDate(week[0]!))} – ${formatter.format(utcDate(week[6]!))}`
}

export function shortCalendarDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(utcDate(value))
}

export function calendarDayNumber(value: string) {
  return utcDate(value).getUTCDate()
}

export function calendarWeekday(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(utcDate(value))
}

export function isSameMonth(value: string, anchor: string) {
  const left = utcDate(value)
  const right = utcDate(anchor)
  return (
    left.getUTCMonth() === right.getUTCMonth() &&
    left.getUTCFullYear() === right.getUTCFullYear()
  )
}

export function appointmentMinute(appointment: AppointmentItem) {
  if (appointment.scheduledAtMs === null) return null
  return minuteOfDay(appointment.scheduledAtMs, appointment.clinicTimeZone)
}

export function appointmentTimeLabel(appointment: AppointmentItem) {
  if (appointment.scheduledAtMs === null) return null
  let timeZone = appointment.clinicTimeZone ?? 'UTC'
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
  } catch {
    timeZone = 'UTC'
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(appointment.scheduledAtMs)
}

export function appointmentProviderKey(appointment: AppointmentItem) {
  return appointment.providerName ?? '__clinic_schedule__'
}

export const NO_ROOM_LANE = '__no_room__'

export function appointmentLaneKey(
  appointment: AppointmentItem,
  grouping: CalendarGrouping
) {
  return grouping === 'room'
    ? (appointment.operatoryName ?? NO_ROOM_LANE)
    : appointmentProviderKey(appointment)
}

export function laneLabel(key: string) {
  return key === '__clinic_schedule__'
    ? 'Clinic schedule'
    : key === NO_ROOM_LANE
      ? 'No room'
      : key
}

/** Lane order: by name, with numbers in natural order (OP-2 before OP-10). */
export function compareLanes(left: string, right: string) {
  return laneLabel(left).localeCompare(laneLabel(right), 'en-US', {
    numeric: true,
    sensitivity: 'base',
  })
}

const COUNT_FORMAT = new Intl.NumberFormat('en-US')

export function formatCount(count: number) {
  return COUNT_FORMAT.format(count)
}

/** "1 appointment", "2,179 appointments". */
export function countLabel(count: number, word: string) {
  return `${formatCount(count)} ${word}${count === 1 ? '' : 's'}`
}

export function isBookedByFlio(appointment: AppointmentItem) {
  return ['sms', 'voice', 'dashboard'].includes(appointment.source)
}

/** Minutes after the clinic's local midnight. */
export function minuteOfDay(timestamp: number, timeZone: string | null) {
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone ?? 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
  } catch {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
  }
  const values = Object.fromEntries(
    formatter
      .formatToParts(timestamp)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
  const hour = Number(values.hour)
  const minute = Number(values.minute)
  return Number.isFinite(hour) && Number.isFinite(minute)
    ? hour * 60 + minute
    : null
}

/** The clinic's short zone name at that moment, such as "EDT". */
export function timeZoneAbbreviation(
  timestamp: number,
  timeZone: string | null
) {
  if (!timeZone) return null
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
        .formatToParts(timestamp)
        .find((part) => part.type === 'timeZoneName')?.value ?? null
    )
  } catch {
    return null
  }
}

export function isCalendarDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  )
}

export function isCalendarView(value: string | null): value is CalendarView {
  return value === 'day' || value === 'week' || value === 'month'
}

export function appointmentMatches(
  appointment: AppointmentItem,
  provider: string,
  status: CalendarStatusFilter
) {
  const providerMatches =
    provider === 'all' || appointmentProviderKey(appointment) === provider
  const statusMatches =
    status === 'all' ||
    (status === 'active'
      ? appointment.status !== 'cancelled'
      : status === 'flio'
        ? isBookedByFlio(appointment)
        : appointment.status === status)
  return providerMatches && statusMatches
}

export function activitiesFromConversations(
  conversations: Conversation[],
  timeZone: string | null
) {
  return conversations.map((conversation): CalendarActivity => ({
    id: conversation.conversationId,
    aggregateId: conversation.aggregateId,
    timestamp: conversation.timestamp,
    date: calendarDateKey(conversation.timestamp, timeZone),
    displayName: conversation.details.displayName,
    preview: conversation.preview,
    channel: conversation.channel,
    timeZone,
  }))
}
